import type { AssemblyView } from './assemblyScene';
import { assemblyOf, type Movement } from './assemblyStore';
import type { Vec3 } from './assemblyPose';
import { TOUCH_MM } from './assemblyClash';

// SEATING A PART AGAINST THE SURFACE IT ACTUALLY MEETS.
//
// WHAT THE CONTACT GETS RIGHT AND WHAT IT CANNOT. Phase 5 asks a model where two parts touch
// — which face of each, and whereabouts on it — and that is a question a reader of a diagram
// can answer: the rail meets the front of the frame, low down, towards the left leg. The app
// then places one named point on the other, and the parts touch.
//
// Their BOUNDING BOXES touch. A box face is a plane through the furthest point of a part, and
// a chair frame's furthest point forward is the bulge of its curved backrest — while the leg
// the rail actually lands on stands several centimetres behind that plane. So the arithmetic
// was exact and the picture still showed a rail hanging in mid air, which is the same failure
// the poses had, arrived at from the opposite direction.
//
// SO THE LAST NUMBER IS MEASURED RATHER THAN NAMED. The contact fixes the two lateral
// coordinates and the direction of approach — the things a diagram shows — and how far IN the
// part goes is settled here, against the triangles, by sliding it along that direction until
// the measured clearance lands in the tolerance band. The model answers what it can see; the
// app answers what it can measure. Nobody guesses.
//
// IT IS THE SAME MEASUREMENT THE CLEARANCE GRAPH DRAWS, one moment at a time (see
// clashProfile, which takes the moments to sample). So a fitting seated here reads "touching"
// on the bar under it because it is the same number, not because two checks agreed.

// HOW FAR IN OR OUT THE SEARCH WILL EVER GO, in millimetres. A part that has to travel more
// than this to meet the thing it is being held against is not a part whose contact was read
// slightly wrong — it is a contact on the wrong face, and sliding it a third of a metre would
// turn a visible mistake into an invisible one somewhere else.
const REACH_MM = 300;

// AND HOW FINELY. Twelve halvings of the bracket puts the answer inside a tenth of a
// millimetre of first contact from any starting distance this allows, which is well under the
// quarter-millimetre the whole app calls touching.
const STEPS = 12;

export type Seated = { seat: Vec3; travel: number };

// WHERE THE MOVING PART WOULD REST IF IT WERE SLID `d` MILLIMETRES ALONG THE APPROACH.
//
// `axis` points from the rest towards the start — outwards — so a positive `d` backs the part
// off and a negative one pushes it home. The travel moves with it in the opposite sense, which
// is what keeps the part STARTING where the model said it starts: the movement gets longer as
// the resting place goes deeper, and the animation still begins in the same place.
const shifted = (f: Movement, d: number): Seated => ({
  seat: [f.seat[0] + f.axis[0] * d, f.seat[1] + f.axis[1] * d, f.seat[2] + f.axis[2] * d],
  travel: Math.max(0, f.travel - d),
});

// The clearance at rest, in millimetres: positive apart, negative one part inside the other.
// Null when there is nothing to measure — a scene that has not finished loading, or a fitting
// with one part in it.
async function gapAt(
  view: AssemblyView,
  f: Movement,
  at: Seated,
): Promise<number | null> {
  // MEASURED AT t = 1 AND NOWHERE ELSE. What is being sought is where the part comes to REST;
  // the approach it makes on the way there is the clearance graph's question, and it is asked
  // again in full once the resting place is settled.
  const profile = await view.clash(assemblyOf({ ...f, ...at }), 1, [1]);
  const said = profile.samples[0];
  return said && Number.isFinite(said.mm) ? said.mm : null;
}

// SLIDE IT HOME. Returns where the part should rest, or null when the surfaces never meet
// within reach — which is a contact worth showing as it was answered rather than one worth
// shoving somewhere plausible.
//
// TWO PHASES, and the first is the one that does the work: step inward in doubling strides
// until the part is touching or through the other, so a part that starts 40 mm out and one
// that starts 4 mm out both bracket in a handful of measurements. Then halve the bracket
// until the gap is inside the tolerance.
//
// A PART THAT ALREADY OVERLAPS IS BACKED OUT by the same walk, in the other direction: the
// contact's two lateral coordinates are just as likely to have landed it inside the frame as
// short of it, and "seat this" means the same thing either way.
export async function seatOnSurface(
  view: AssemblyView,
  f: Movement,
): Promise<Seated | null> {
  const here: Seated = { seat: f.seat, travel: f.travel };
  const first = await gapAt(view, f, here);
  if (first == null) return null;
  if (Math.abs(first) <= TOUCH_MM) return here;

  // WHICH WAY HOME IS. Apart means in, through means out — and the sign of the walk is the
  // only thing that differs between the two cases.
  const sign = first > 0 ? -1 : 1;
  let near = 0;
  let far = 0;
  let step = 1;
  // The bracket: `near` is a distance whose gap still has the starting sign, `far` the first
  // one past it. A stride that reaches REACH without crossing is a contact that does not meet.
  for (;;) {
    const d = sign * step;
    const said = await gapAt(view, f, shifted(f, d));
    if (said == null) return null;
    if (Math.abs(said) <= TOUCH_MM) return shifted(f, d);
    if (Math.sign(said) !== Math.sign(first)) { far = d; break; }
    near = d;
    step *= 2;
    if (step > REACH_MM) return null;
  }

  // AND HALVE IT. Every iteration keeps one end apart and the other through, so the answer is
  // always between them and always closer than it was.
  for (let i = 0; i < STEPS; i += 1) {
    const mid = (near + far) / 2;
    const said = await gapAt(view, f, shifted(f, mid));
    if (said == null) return null;
    if (Math.abs(said) <= TOUCH_MM) return shifted(f, mid);
    if (Math.sign(said) === Math.sign(first)) near = mid;
    else far = mid;
  }
  // TOUCHING IS WHAT IS WANTED AND THE FAR END IS WHAT IS SAFE: half a bracket of this width
  // is under a tenth of a millimetre, and of the two ends the one that is through the other is
  // the one the clearance bar would call an overlap.
  return shifted(f, near);
}
